import InventoryItemDealerPortalTypeEnum from 'enums/InventoryItemDealerPortalTypeEnum';
import React from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import Cart from 'routes/Pages/Cart/Cart';
import Checkout from 'routes/Pages/Checkout/Checkout';
import Profile from 'routes/Pages/Profile/Profile';
import InventoryItemCategory from 'routes/Pages/InventoryItemCategories/InventoryItemCategory';
import { usePrefetchCart } from 'services/Data/CartService';
import Wall from 'routes/Pages/Wall/Wall';
import Tooling from 'routes/Pages/Tooling/Tooling';
import HomePage from 'routes/Pages/HomePage/HomePage';
import ExteriorSignageQuoteLandingInfo from 'routes/Pages/ExteriorSignageQuote/ExteriorSignageQuoteLandingInfo';
import ExteriorSignageQuoteCheckout from 'routes/Pages/ExteriorSignageQuote/ExteriorSignageQuoteCheckout';
import MerchandisingProgramQuoteLandingInfo from 'routes/Pages/MerchandisingProgramQuote/MerchandisingProgramQuoteLandingInfo';
import MerchandisingProgramQuote from 'routes/Pages/MerchandisingProgramQuote/MerchandisingProgramQuote';
import MerchandisingProgramQuoteCheckout from 'routes/Pages/MerchandisingProgramQuote/MerchandisingProgramQuoteCheckout';
import UsefulLinks from 'routes/Pages/UsefulLink/UsefulLinks';
import Supports from 'routes/Pages/Support/Supports';
import Certification from 'routes/Pages/Certification/Certification';
import DealerPortalMenuProducts from 'routes/Pages/DealerPortalMenu/DealerPortalMenuProducts';
import DealerPortalMenuProductDetail from 'routes/Pages/DealerPortalMenu/DealerPortalMenuProductDetail';
import DealerPortalSectionLandingPage from 'routes/Pages/DealerPortalMenu/DealerPortalSectionLandingPage';
import ShowroomProjectRequest from 'routes/Pages/ShowroomProjectRequest/ShowroomProjectRequest';
import { usePrefetchApprovalNotification } from 'services/Data/NotificationService';
import { useDealerPortalSectionLandingPage } from 'services/Data/DealerPortalMenuServices';
import { homePage, IsClub, IsDSM, IsImpersonationMode, IsMerchandisingProgram, IsProduction, PODUrl } from 'config/config';
import { useSelector } from 'react-redux';
import { getDealerPortalSectionValue } from 'enums/DealerPortalSectionEnum';
import { isPrintOnDemandSection, shouldUseLegacyPodUrl } from 'services/Utilities/podRoutingUtils';

const LegacyPodRedirect = () => {
  if (typeof window !== 'undefined') {
    window.location.replace(PODUrl);
  }

  return null;
};

const buildSkipLandingPath = location => {
  const searchParams = new URLSearchParams(location.search || '');
  searchParams.set('skipLanding', '1');
  const search = searchParams.toString();

  return `${location.pathname}${search ? `?${search}` : ''}`;
};

const DealerPortalMenuRouteGuard = ({ routeProps, isProductDetail = false }) => {
  const { sectionName } = routeProps?.match?.params || {};
  const sectionValue = getDealerPortalSectionValue(sectionName);
  const searchParams = new URLSearchParams(routeProps?.location?.search || '');
  const isFromHomeMenu = searchParams.get('fromHomeMenu') === '1';
  const skipLanding = searchParams.get('skipLanding') === '1';
  const shouldCheckLanding = isFromHomeMenu && !skipLanding && sectionValue > 0;
  const { data: sectionLandingData, isLoading: isLandingLoading } = useDealerPortalSectionLandingPage(
    sectionValue,
    shouldCheckLanding,
  );

  if (isPrintOnDemandSection(sectionName) && shouldUseLegacyPodUrl()) {
    return <LegacyPodRedirect />;
  }

  if (IsMerchandisingProgram && String(sectionName || '').toLowerCase() === 'displaystoolingandfurniture') {
    return <Redirect to={homePage} />;
  }

  if (shouldCheckLanding && isLandingLoading) {
    return null;
  }

  const hasSectionLandingPage = Boolean(sectionLandingData?.hasSectionLandingPage);
  const isSectionLandingActive = sectionLandingData?.sectionLandingIsActive ?? hasSectionLandingPage;

  if (shouldCheckLanding && hasSectionLandingPage && isSectionLandingActive) {
    const targetPath = buildSkipLandingPath(routeProps.location);
    return <Redirect to={`/dealer/section/${sectionName}/landing?target=${encodeURIComponent(targetPath)}`} />;
  }

  if (isProductDetail) {
    return <DealerPortalMenuProductDetail {...routeProps} />;
  }

  return <DealerPortalMenuProducts {...routeProps} />;
};

const ApplicationLayout = () => {
  const accountReducer = useSelector(state => state.AccountReducer);

  const dealerId = accountReducer?.profile?.dealer_Id;
  const activeDealerId = accountReducer?.profile?.temporaryDealer_Id || dealerId;
  usePrefetchCart(accountReducer.isLoggedIn, IsImpersonationMode ? dealerId : null);
  usePrefetchApprovalNotification(accountReducer.isLoggedIn);

  const getInventoryTypeEditComponent = categoryName => {
    switch (categoryName) {
      case 'Walls':
        return Wall;
      case 'Tooling':
        return Tooling;
      case 'OtherTooling':
      case 'MountedDisplay':
        return Tooling;
      case 'WallPanelComponent':
      case 'MerchandisingTooling':
      case 'SignHolder':
      case 'FloorDisplay':
      case 'Furniture':
      case 'PartsServices':
      case 'VehiclePurpose':
      case 'Ceiling':
        return InventoryItemCategory;

      default:
        break;
    }
  };

  return (
    <Switch>
      <Route path="/dealer/clearances/:inventoryItemId" component={InventoryItemCategory} />
      <Route path="/dealer/cart/checkout" component={Checkout} />
      <Route path="/dealer/cart" component={Cart} />
      <Route path="/dealer/useful-links" component={UsefulLinks} />
      <Route path="/dealer/support-chats" component={Supports} />
      <Route path="/dealer/profile" component={Profile} />
      <Route
        path="/dealer/home-page/exterior-signage/check-out/:exteriorSignageQuoteId"
        component={ExteriorSignageQuoteCheckout}
      />
      <Route path="/dealer/home-page/exterior-signage/check-out" component={ExteriorSignageQuoteCheckout} />
      {/* <Route path="/dealer/home-page/exterior-signage/quote" component={ExteriorSignageQuote} />
        <Route path="/dealer/home-page/exterior-signage" component={ExteriorSignageQuoteLandingInfo} /> */}
      <Route path="/dealer/home-page/merchandising-program/check-out" component={MerchandisingProgramQuoteCheckout} />
      <Route path="/dealer/home-page/merchandising-program/quote" component={MerchandisingProgramQuote} />
      <Route path="/dealer/home-page/merchandising-program" component={MerchandisingProgramQuoteLandingInfo} />
      {/* REMOVE WHEN READY TO SHOW NEW PAGE -- IT WILL SIMPLY WORK IF ITS REMOVED */}
      {/* {IsProduction && (!IsImpersonationMode || IsDSM) && (
        <Route path="/dealer/section/ExteriorSignage/menu/:dealerPortalMenuId" component={ExteriorSignageQuoteLandingInfo} />
      )} */}

      {/* REMOVE WHEN READY TO SHOW NEW PAGE -- IT WILL SIMPLY WORK IF ITS REMOVED */}
      {IsClub && (
        <Route
          path="/dealer/section/MerchandisingServices/menu/:dealerPortalMenuId"
          component={MerchandisingProgramQuoteLandingInfo}
        />
      )}

      {/* REMOVE WHEN READY TO SHOW NEW PAGE -- IT WILL SIMPLY WORK IF ITS REMOVED */}
      {IsProduction && (!IsImpersonationMode || IsDSM) && (
        <Route
          path="/dealer/section/MerchandisingServices/menu/:dealerPortalMenuId"
          component={MerchandisingProgramQuoteLandingInfo}
        />
      )}

      <Route
        path="/dealer/section/:sectionName/menu/:dealerPortalMenuId/products/:dealerPortalMenuProductId"
        render={routeProps => <DealerPortalMenuRouteGuard routeProps={routeProps} isProductDetail />}
      />

      <Route
        path="/dealer/section/:sectionName/menu/:dealerPortalMenuId"
        render={routeProps => <DealerPortalMenuRouteGuard routeProps={routeProps} />}
      />
      <Route path="/dealer/section/:sectionName/landing" component={DealerPortalSectionLandingPage} />
      <Route
        path="/dealer/home-page/showroom-project-request"
        render={routeProps => <ShowroomProjectRequest key={activeDealerId} {...routeProps} />}
      />
      <Route path="/dealer/home-page" component={HomePage} />
      <Route path="/dealer/certification" render={routeProps => <Certification {...routeProps} />} />
      {InventoryItemDealerPortalTypeEnum.filter(cat => cat.shownInNavBar && !cat.parentCategory).map(cat => {
        const childrenRoute = InventoryItemDealerPortalTypeEnum.filter(
          c => c.shownInNavBar && c.parentCategory === cat.value,
        );
        if (childrenRoute.length > 0) {
          return childrenRoute.map(scat => {
            const subchildRoute = InventoryItemDealerPortalTypeEnum.filter(
              c => c.shownInNavBar && c.parentCategory === scat.value,
            );

            if (subchildRoute.length > 0) {
              return (
                <Route
                  key={scat.value}
                  path={`/dealer/${scat.route}/:category/:inventoryItemId`}
                  component={getInventoryTypeEditComponent(scat.name)}
                  exact
                />
              );
            }

            return (
              <Route
                key={scat.value}
                path={`/dealer/${cat.route}/:category/:inventoryItemId`}
                component={getInventoryTypeEditComponent(scat.name)}
                exact
              />
            );
          });
        } else {
          return (
            <Route
              key={cat.value}
              path={`/dealer/${cat.route}/:inventoryItemId`}
              component={getInventoryTypeEditComponent(cat.name)}
              exact
            />
          );
        }
      })}
      <Route path="/dealer" component={HomePage} />

      <Redirect to={homePage} />
    </Switch>
  );
};

export default ApplicationLayout;
